// 360° feedback — HR/manager view (Phase 4c). Open cycles, track responses, read
// aggregated results.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Users } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

export default function Feedback360Admin({ showToast }) {
  const [rows, setRows] = useState(null);
  const [people, setPeople] = useState([]);
  const [creating, setCreating] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.feedbackCycles().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => setPeople([])); }, []);

  const create = async () => {
    if (!creating.subjectId || busy) return; setBusy(true);
    try {
      const peerIds = Object.keys(creating.peers || {}).filter((k) => creating.peers[k]);
      await api.openFeedbackCycle({ subjectId: creating.subjectId, title: creating.title, peerIds });
      showToast?.('Feedback cycle opened'); setCreating(null); load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const open = (id) => api.feedbackCycle(id).then(setResults).catch((e) => showToast?.(e.message));
  const close = async (id) => { try { await api.closeFeedbackCycle(id); open(id); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="360° Feedback" sub="Multi-rater feedback cycles" />
        <PrimaryBtn onClick={() => setCreating({ subjectId: '', title: '', peers: {} })}><Plus size={14} style={{ marginRight: 6 }} /> New cycle</PrimaryBtn>
      </div>

      {rows.length === 0 ? (
        <Card><EmptyState icon={Users} title="No feedback cycles" desc="Open a cycle to gather 360° feedback for someone." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((c) => (
            <Card key={c.id} style={{ padding: 16, cursor: 'pointer' }} onClick={() => open(c.id)}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{c.subjectName}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{c.title}</div>
                </div>
                <Pill tone={c.status === 'Open' ? 'info' : 'neutral'}>{c.status}</Pill>
              </div>
              <div className="flex items-center justify-between" style={{ marginTop: 12, fontSize: 13 }}>
                <span style={{ color: AG.n500 }}>{c.submitted}/{c.total} responded · {c.responseRate}%</span>
                <span style={{ fontWeight: 700, color: AG.primary }}>{c.avgRating != null ? `${c.avgRating}/5` : '—'}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={!!creating} onClose={() => !busy && setCreating(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New feedback cycle</DialogTitle>
        <DialogContent>
          {creating && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Subject</div>
              <select value={creating.subjectId} onChange={(e) => setCreating({ ...creating, subjectId: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>
                <option value="">Select employee…</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.dept ? ` · ${p.dept}` : ''}</option>)}
              </select>
              <TextField fullWidth size="small" label="Title (optional)" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} sx={{ mb: 1.5 }} />
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Peers (self + manager auto-included)</div>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${AG.n200}`, borderRadius: 8, padding: 8 }}>
                {people.filter((p) => p.id !== creating.subjectId).map((p) => (
                  <FormControlLabel key={p.id} control={<Checkbox size="small" checked={!!creating.peers?.[p.id]} onChange={(e) => setCreating({ ...creating, peers: { ...creating.peers, [p.id]: e.target.checked } })} />} label={`${p.name}`} sx={{ display: 'flex' }} />
                ))}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setCreating(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={create} disabled={busy || !creating?.subjectId}>Open cycle</PrimaryBtn></DialogActions>
      </Dialog>

      {/* Results dialog */}
      <Dialog open={!!results} onClose={() => setResults(null)} maxWidth="sm" fullWidth>
        {results && (
          <>
            <DialogTitle>{results.subjectName} · {results.title}</DialogTitle>
            <DialogContent>
              <div className="grid grid-cols-3 gap-2" style={{ marginBottom: 12 }}>
                {[['Avg', results.avgRating != null ? `${results.avgRating}/5` : '—'], ['Responses', `${results.submitted}/${results.total}`], ['Rate', `${results.responseRate}%`]].map(([l, v]) => (
                  <Card key={l} style={{ padding: 12, textAlign: 'center' }}><div style={{ fontWeight: 700, color: AG.text }}>{v}</div><div style={{ fontSize: 11, color: AG.n500 }}>{l}</div></Card>
                ))}
              </div>
              {Object.keys(results.relationAvg).length > 0 && (
                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
                  {Object.entries(results.relationAvg).map(([rel, v]) => <Pill key={rel} tone="info">{rel}: {v}/5</Pill>)}
                </div>
              )}
              <div style={{ display: 'grid', gap: 8 }}>
                {results.responses.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No responses yet.</p> : results.responses.map((r, i) => (
                  <div key={i} style={{ border: `1px solid ${AG.n200}`, borderRadius: 10, padding: 12 }}>
                    <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                      <Pill tone="neutral">{r.relation}{r.raterName ? ` · ${r.raterName}` : ''}</Pill>
                      <span style={{ fontWeight: 700, color: AG.primary }}>{r.rating}/5</span>
                    </div>
                    {r.strengths && <div style={{ fontSize: 13, color: AG.text }}><b style={{ color: AG.n600 }}>Strengths:</b> {r.strengths}</div>}
                    {r.improvements && <div style={{ fontSize: 13, color: AG.text }}><b style={{ color: AG.n600 }}>Improve:</b> {r.improvements}</div>}
                  </div>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              {results.status === 'Open' && <OutlineBtn onClick={() => close(results.id)}>Close cycle</OutlineBtn>}
              <PrimaryBtn onClick={() => setResults(null)}>Done</PrimaryBtn>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
}
